import { useMemo } from 'react';
import { parseISO, format } from 'date-fns';
import { Loader2, Users } from 'lucide-react';
import type { TFunction } from 'i18next';

import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getDateLocale, getDateFormats } from '@/utils/dateLocale';
import type { GroupOccurrence } from '@/services/api/admin-booking.service';

interface GroupOccurrencesStepProps {
  occurrences: GroupOccurrence[];
  selected: Set<number>;
  onToggle: (slotId: number) => void;
  onToggleAll: () => void;
  isLoading: boolean;
  // Seats the batch needs (number of selected customers). A session is only
  // selectable if it has at least this many spots left.
  requiredSeats: number;
  t: TFunction;
}

// A group occurrence is selectable only when it isn't full, the customer isn't
// already enrolled, and it has room for every selected customer.
export function isSelectable(o: GroupOccurrence, requiredSeats = 1): boolean {
  return !o.full && !o.customer_already_booked && o.spots_remaining >= Math.max(1, requiredSeats);
}

export function GroupOccurrencesStep({
  occurrences,
  selected,
  onToggle,
  onToggleAll,
  isLoading,
  requiredSeats,
  t,
}: GroupOccurrencesStepProps) {
  const dateLocale = getDateLocale();
  const dateFormats = getDateFormats();

  const selectableIds = useMemo(
    () => new Set(occurrences.filter((o) => isSelectable(o, requiredSeats)).map((o) => o.slot_id)),
    [occurrences, requiredSeats],
  );
  const selectableCount = selectableIds.size;
  // Count only currently-selectable selections — stale ids (e.g. a slot that
  // went over capacity after more customers were added) shouldn't inflate the
  // "n of total" readout or trip "Unselect all".
  const selectedSelectableCount = useMemo(() => {
    let n = 0;
    selected.forEach((id) => {
      if (selectableIds.has(id)) n += 1;
    });
    return n;
  }, [selected, selectableIds]);
  const allSelected = selectableCount > 0 && selectedSelectableCount >= selectableCount;

  if (isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-brand-500" />
      </div>
    );
  }

  if (occurrences.length === 0) {
    return (
      <Alert>
        <AlertDescription>
          {t('groupRecurring.noOccurrences', 'No upcoming sessions are scheduled for this group service.')}
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4" data-testid="group-occurrences">
      <div className="flex items-center justify-between">
        <p className="text-body-sm text-soft">
          {t('groupRecurring.selectedCount', '{{count}} of {{total}} selected', {
            count: selectedSelectableCount,
            total: selectableCount,
          })}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onToggleAll}
          disabled={selectableCount === 0}
          data-testid="group-toggle-all"
        >
          {allSelected
            ? t('groupRecurring.unselectAll', 'Unselect all')
            : t('groupRecurring.selectAll', 'Select all')}
        </Button>
      </div>

      <div className="rounded-2xl border border-line divide-y divide-line">
        {occurrences.map((o) => {
          const start = parseISO(o.start_time);
          const end = parseISO(o.end_time);
          const selectable = isSelectable(o, requiredSeats);
          const checked = selected.has(o.slot_id);
          // Not full, but can't fit the whole selected group.
          const overCapacity = !o.full && !o.customer_already_booked && o.spots_remaining < requiredSeats;

          return (
            <label
              key={o.slot_id}
              className={cn(
                'flex items-center gap-3 px-4 py-3 transition-colors',
                selectable ? 'cursor-pointer hover:bg-surface-2' : 'cursor-not-allowed opacity-70',
                checked && 'bg-brand-50',
              )}
              data-testid="group-occurrence-row"
            >
              <Checkbox
                checked={checked}
                disabled={!selectable}
                onCheckedChange={() => selectable && onToggle(o.slot_id)}
                data-testid="group-occurrence-checkbox"
              />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-ink">
                  {format(start, dateFormats.fullWithWeekday, { locale: dateLocale })}
                </p>
                <p className="text-body-sm text-soft">
                  {format(start, 'HH:mm')} – {format(end, 'HH:mm')}
                  {o.provider?.name ? ` · ${o.provider.name}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                {o.customer_already_booked ? (
                  <Badge tone="neutral">{t('groupRecurring.alreadyBooked', 'Already booked')}</Badge>
                ) : o.full ? (
                  o.waitlist_enabled ? (
                    <Badge className="bg-amber-100 text-amber-800 border border-amber-200">
                      {t('groupRecurring.fullWaitlist', 'Full · waitlist')}
                    </Badge>
                  ) : (
                    <Badge variant="destructive">{t('groupRecurring.full', 'Full')}</Badge>
                  )
                ) : overCapacity ? (
                  <Badge variant="destructive" data-testid="group-over-capacity">
                    {t('groupRecurring.notEnoughSeats', 'Can\'t fit {{needed}} · {{left}} left', {
                      needed: requiredSeats,
                      left: o.spots_remaining,
                    })}
                  </Badge>
                ) : (
                  <span className="inline-flex items-center gap-1 text-body-sm text-soft">
                    <Users className="h-3.5 w-3.5" />
                    {t('groupRecurring.spotsLeft', '{{count}} left', { count: o.spots_remaining })}
                  </span>
                )}
              </div>
            </label>
          );
        })}
      </div>

      <p className="text-body-xs text-soft">
        {t(
          'groupRecurring.fullHint',
          'Full sessions can\'t be selected here. Use the single booking flow to add the customer to a waitlist.',
        )}
      </p>
    </div>
  );
}
